import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { isEmpty } from 'lodash'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { usePermissions } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData, TableStateData } from 'utils/TableStateContext'

import {
  CreateNotificationContactDetailsDto,
  NotificationContactDetailsDto,
  UpdateNotificationContactDetailsDto,
} from '../../../api/Notifications/Types/NotificationContactDetailsDto'
import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { errorToast, successToast, ToastContext } from '../../../components/Toast/Toast'
import { NotificationsApiClient } from '../../../utils/api/notificationsApi'
import { GridColumnProps, useContactDetailsColumns } from './contactDetailsColumns'
import {
  ContactDetailsForm,
  isContactDetailsNew,
  isContactDetailsUpdate,
} from './ContactDetailsForm'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

type DataGridQueryProps<T> = {
  data: T[]
  setData(data: T[]): void
  fetchData(): Promise<void>
  getQuery: (filter?: CompositeFilterDescriptor) => string | null
  getSortQuery: (sortDescriptors?: SortDescriptor[]) => string | null
  filterFetch: (params?: { query?: string | null }) => Promise<void>
}

type GridStatusStateProps = ReturnType<typeof useGridState>
type DataStateProps = ReturnType<typeof useGridStatusState>
type DataGridFetchProps<T> = GridStatusStateProps &
  DataStateProps &
  TableStateData &
  DataGridQueryProps<T>
type StateToProps = ReturnType<typeof useMapStateToProps>
type DataGridStateProps<T> = StateToProps & DataGridFetchProps<T>
type PageFetchProps<T> = DataGridFetchProps<T> & DataGridStateProps<T>

const useContactDetailsFetch = (): ContactDetailsFetchProps => {
  const notificationsApiClient = useApiClient(NotificationsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [data, setData] = useState<NotificationContactDetailsDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { fetchTickmillCompanies } = useMapDispatchToProps()
  const { tickmillCompanies } = useMapStateToProps()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  useEffect(() => {
    fetchData()
    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompanies()
    }
  }, [])

  const fetchData = async () => {
    await fetchGrid(
      async (
        gridFilterState: GridFilterActionState
      ): Promise<PageData<NotificationContactDetailsDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await notificationsApiClient.getNotificationsContactDetails({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setData(response.items)
        }

        setTableData({ ...tableData, meta: response })
        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<NotificationContactDetailsDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSortQuery(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await notificationsApiClient.getNotificationsContactDetails({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setData(response.items.map((x) => ({ ...x, selected: false })))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // tslint:disable-next-line:max-line-length
    [
      getQuery,
      getSortQuery,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    data,
    setData,
    gridState,
    gridActions,
    tableData,
    setTableData,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
    filterFetch,
    tickmillCompanies,
    fetchData,
  }
}

type ContactDetailsFetchProps = PageFetchProps<NotificationContactDetailsDto>

const initialModalState = { data: undefined, visible: false }

interface ModalProps {
  visible: boolean
  data?: NotificationContactDetailsDto
}

export const useContactDetailsModal = () => {
  const [contactDetailsModal, setContactDetailsModal] = useState<ModalProps>(initialModalState)

  const handleAddContactDetailsModalOpen = () => {
    setContactDetailsModal({
      data: undefined,
      visible: true,
    })
  }

  const handleUpdateContactDetailsModalOpen = (data?: NotificationContactDetailsDto) => {
    setContactDetailsModal({
      data,
      visible: true,
    })
  }

  const handleContactDetailsModalClose = () => {
    setContactDetailsModal({
      data: undefined,
      visible: false,
    })
  }

  return {
    contactDetailsModal,
    handleAddContactDetailsModalOpen,
    handleUpdateContactDetailsModalOpen,
    handleContactDetailsModalClose,
  }
}

export const useAuditLogModal = () => {
  const [auditLogModal, setAuditLogModal] = useState<ModalProps>(initialModalState)

  const handleAuditLogOpen = (record: NotificationContactDetailsDto) => {
    setAuditLogModal({ data: record, visible: true })
  }

  const handleAuditLogClose = () => {
    setAuditLogModal({
      data: undefined,
      visible: false,
    })
  }

  return {
    auditLogModal,
    handleAuditLogOpen,
    handleAuditLogClose,
  }
}

export const ContactDetailsPage = () => {
  const crumbs = [{ title: 'Contact Details', link: links.administrationContactDetails.url }]

  const { hasPermission } = usePermissions()

  const contactDetailsModalProps = useContactDetailsModal()
  const auditLogModalProps = useAuditLogModal()
  const modalsProps = Object.assign({}, contactDetailsModalProps, auditLogModalProps)
  const contactDetailsColumnsProps = useContactDetailsColumns(modalsProps)

  const contactDetailsFetch = useContactDetailsFetch()
  const { handleSkipCountChange, handlePageSizeChange, handleGridPageChange } =
    useDataGridMutate(contactDetailsFetch)

  return (
    <>
      <Modals {...contactDetailsFetch} {...modalsProps} />
      <Breadcrumbs crumbs={crumbs} />
      {hasPermission(PermissionsEnum.CanCreatePlatformLinks) && (
        <AddButton text='Add Entity' onClick={modalsProps.handleAddContactDetailsModalOpen} />
      )}
      <DataGrid
        {...contactDetailsFetch}
        columns={contactDetailsColumnsProps.contactDetailsColumns}
        onPageChange={handleGridPageChange}
        onPageSizeChange={handlePageSizeChange}
        onCountChange={handleSkipCountChange}
      />
    </>
  )
}

type ModalsProps = ReturnType<typeof useContactDetailsModal> & ReturnType<typeof useAuditLogModal>
type ContactDetailsModalsProps = ModalsProps & ContactDetailsFetchProps

const Modals: React.FC<ContactDetailsModalsProps> = (props) => {
  const { contactDetailsModal, handleContactDetailsModalClose } = props
  const { auditLogModal, handleAuditLogClose } = props
  const { tickmillCompanies } = props

  const notificationsApiClient = useApiClient(NotificationsApiClient)
  const setToast = useContext(ToastContext)

  const handleSubmit = async (
    values: CreateNotificationContactDetailsDto | UpdateNotificationContactDetailsDto
  ) => {
    try {
      if (isContactDetailsNew(values)) {
        await notificationsApiClient.addNotificationContactDetails(values)
        setToast(successToast('Contact Details added successfully.'))
      }

      if (isContactDetailsUpdate(values)) {
        await notificationsApiClient.updateNotificationsContactDetails(
          values.tickmillCompanyId,
          values
        )
        setToast(successToast('Contact Details updated successfully.'))
      }

      handleContactDetailsModalClose()
    } catch (error) {
      if (error instanceof ResponseError && error?.response?.message) {
        setToast(errorToast(error.response.message))
        console.error('Failed to submit contact details:', error)
      }
    } finally {
      props.fetchData()
    }
  }

  return (
    <>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          notificationContactDetailsId={auditLogModal.data.tickmillCompany.id}
          isVisible={auditLogModal.visible}
          onClose={handleAuditLogClose}
        />
      )}
      {contactDetailsModal.visible && (
        <ContactDetailsForm
          isOpen={contactDetailsModal.visible}
          onClose={handleContactDetailsModalClose}
          data={contactDetailsModal.data}
          tickmillCompanies={tickmillCompanies}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}

const useDataGridMutate = (props: DataGridStateProps<NotificationContactDetailsDto>) => {
  const notificationsApiClient = useApiClient(NotificationsApiClient)

  const { tableData, gridState, gridActions, getQuery, getSortQuery } = props
  const { setTableData, setData } = props

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await notificationsApiClient.getNotificationsContactDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setData(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await notificationsApiClient.getNotificationsContactDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await notificationsApiClient.getNotificationsContactDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults
    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return { handleSkipCountChange, handlePageSizeChange, handleGridPageChange }
}

interface DataGridProps extends DataGridStateProps<NotificationContactDetailsDto> {
  columns: GridColumnProps[]
  onPageChange(pageNumber: number): Promise<void>
  onPageSizeChange(pageSize: number): Promise<void>
  onCountChange(skipCount: boolean): Promise<void>
}

const DataGrid: React.FC<DataGridProps> = (props) => {
  const { columns, onPageChange, onPageSizeChange, onCountChange } = props
  const { tableData, gridState, data } = props
  const { setTableData } = props

  return (
    <TableStateContext.Provider value={[tableData, setTableData]}>
      <Grid
        {...gridState}
        resizable
        data={data}
        sort={gridState.sort}
        total={gridState.total}
        take={gridState.take}
        skip={gridState.skip}
        pageable
        pager={() => (
          <Pager
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSkipCountChange={onCountChange}
            page={gridState.pageIndex}
            pageSize={gridState.pageSize}
            take={gridState.take || 10}
            skip={gridState.skip || 0}
          />
        )}
        style={{ zIndex: 0 }}
      >
        {columns.map((column, idx) => (
          <GridColumn key={idx} {...column} />
        ))}
      </Grid>
    </TableStateContext.Provider>
  )
}
